// dependencies
import "ag-grid-enterprise";
import Select from "react-select";
import React, { useRef, useState, StrictMode, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react"; // React Grid Logic
import { useWindowSize } from "@uidotdev/usehooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// components
import Grid from "../../../../Components/Grid";
import Button from "../../../../Components/Button";
import Spacer from "../../../../Components/Spacer";
import { useFeatureFlagMatrix, useFeatureFlags } from "../../../../Api/hooks";
import { useEnvironment } from "../../../../Hooks";
import { updateCompanyFeatureFlag } from "../../../../Api/services/company";
import { CompanyLogoRenderer } from "./internal/company-logo-renderer";
import { CustomStringCellEditor } from "./internal/custom-string-cell-editor";

// css
import css from "./CompanyMatrix.styl";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const categoryOptions = [
	{ value: "permanent", label: "Permanent", categories: ["permanent", "config"] },
	{ value: "ephemeral", label: "Ephemeral", categories: ["ephemeral"] },
	{ value: "all", label: "All", categories: ["permanent", "ephemeral", "config"] },
];

const DEFAULT_COLUMN_DEF = {
	sortable: true,
	filter: true,
};

const COLUMNS = [
	{
		field: "name",
		headerName: "Company",
		pinned: "left",
		sort: "asc",
		cellRenderer: CompanyLogoRenderer,
		cellStyle: (params) => {
			return { color: params.data.icon_color, backgroundColor: params.data.nav_color };
		},
	},
	{
		headerName: "Info",
		children: [
			{
				field: "active_users",
				headerName: "Active Users",
				type: "numericColumn",
			},
		],
	},
];

const CompanyFeatureFlagMatrix = () => {
	const gridRef = useRef(null);
	const size = useWindowSize();
	const queryClient = useQueryClient();
	const environment = useEnvironment();
	const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0]);

	const featureFlagsQuery = useFeatureFlags(environment);
	const featureFlagMatrixQuery = useFeatureFlagMatrix(environment);

	/**
	 * This mutation will update the feature flag of a company.
	 */
	const mutation = useMutation({
		mutationFn: updateCompanyFeatureFlag,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["feature-flag-matrix", environment] });
			featureFlagMatrixQuery.refetch();
		},
	});

	/**
	 * Show the loading overlay if the feature flag matrix or feature flags are
	 * loading.
	 */
	useEffect(() => {
		if (!gridRef.current) return;
		if (!gridRef.current.api) return;

		if (featureFlagMatrixQuery.isLoading || featureFlagsQuery.isLoading) {
			gridRef.current.api.showLoadingOverlay();
			return;
		}

		gridRef.current.api.hideOverlay();
	}, [featureFlagMatrixQuery.data, featureFlagsQuery.data]);

	/**
	 * Filter the flags based on the selected category.
	 */
	const filteredFlags = (featureFlagsQuery.data?.featureFlags ?? []).filter((flag) =>
		selectedCategory.categories.includes(flag.category)
	);

	/**
	 * When a cell is edited, update the value in the matrix. This has to do
	 * some extra work because CSMs have requested that boolean flags use
	 * "Visible" and "Hidden" instead of "true" and "false".
	 */
	const onCellEditRequest = async (event) => {
		const slug = event.colDef.data._id;
		const flagType = event.colDef.data.type;
		const companyId = event.data._id;
		let newValue = event.newValue;

		if (flagType === "boolean") {
			newValue = newValue.toLowerCase() === "visible";
		}

		if (flagType === "number") {
			// no-op
		}

		if (flagType === "string") {
			// no-op
		}

		await mutation.mutateAsync({
			environment: environment,
			companyId: companyId,
			slug: slug,
			data: {
				value: newValue.toString(),
			},
		});
	};

	/**
	 * Setup all the columns for the grid. Each column is a feature flag.
	 */
	const columns = [
		...COLUMNS,
		{
			headerName: "Feature Flags",
			children: filteredFlags.map((flag) => {
				const isNumber = flag.type === "number";

				return {
					field: `feature_flag.${flag._id}`,
					headerName: flag.name,
					data: flag,

					// for some reason, numeric columns are not auto-detected
					...(isNumber ? { type: "numericColumn" } : {}),

					editable: true,
					cellEditorSelector: ({ data }) => {
						if (flag.type === "boolean") {
							return {
								component: "agSelectCellEditor",
								params: {
									values: ["Hidden", "Visible"],
								},
							};
						}

						if (flag.type === "number") {
							return {
								component: "agNumberCellEditor",
							};
						}

						return {
							component: CustomStringCellEditor,
							popup: true,
						};
					},

					/**
					 * This bypasses the default value parser because it is too strict.
					 */
					valueParser: ({ newValue }) => {
						return newValue;
					},
				};
			}),
		},
	];

	/**
	 * Convert boolean values to "Visible" or "Hidden".
	 * We dont't have access to the flag type in the matrix. We could use the
	 * featureFlagsQuery to get the type, but we would have to handle race
	 * conditions. The `boolean` flag type is the only one that uses boolean
	 * values, so we can safely assume that any boolean value is a boolean
	 * flag.
	 */
	const featureFlags = useMemo(() => {
		return (featureFlagMatrixQuery?.data?.featureFlags ?? []).map((company) => {
			const feature_flag = {};

			for (const [key, value] of Object.entries(company.feature_flag ?? {})) {
				if (typeof value === "boolean") {
					feature_flag[key] = value ? "Visible" : "Hidden";
				} else {
					feature_flag[key] = value;
				}
			}

			return {
				...company,
				feature_flag,
			};
		});
	}, [featureFlagMatrixQuery?.data?.featureFlags]);

	return (
		<StrictMode>
			<div className={css.content}>
				<div className={css.sectionInformation}>
					<div className={css.title}>Feature Flags</div>
					<Grid columns={["600px", "1fr", "140px", "120px"]}>
						<div>
							<div className={css.description}>
								Feature flags are used to enable or disable features in the application. This is useful
								for testing features before they are released to the public.
							</div>
						</div>

						<div />

						<Select
							defaultValue={selectedCategory}
							name="category"
							options={categoryOptions}
							isSearchable={false}
							onChange={(option) => setSelectedCategory(option)}
						/>

						<Button href="/feature-flags/create">Add Flag</Button>
					</Grid>

					<Spacer size={3} />

					<div className={css.gridContainer}>
						<div className="ag-theme-quartz" style={{ height: size.height - 220 }}>
							<AgGridReact
								ref={gridRef}
								animateRows={false}
								defaultColDef={DEFAULT_COLUMN_DEF}
								rowData={featureFlags}
								columnDefs={columns}
								onCellValueChanged={onCellEditRequest}
								overlayLoadingTemplate={
									'<div aria-live="polite" aria-atomic="true" style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</StrictMode>
	);
};

export default CompanyFeatureFlagMatrix;
